import { Controller } from "@hotwired/stimulus"
import { shouldIntroduce, permissionText, homeImportStatus } from "../lib/photo_updates_state"

// Keep temporary dismissal through Turbo navigation, but never persist it as consent.
const deferredAccounts = new Set()

// The script handler is the capability handshake. Older app builds do not expose
// it, so their controls stay hidden and their existing import flow is untouched.
export default class extends Controller {
  async connect() {
    this.handler = window.webkit?.messageHandlers?.photoUpdatesV1
    if (!this.handler) return
    this.ready = new Promise(resolve => { this.finishBootstrap = resolve })
    this.disconnected = false
    this.busy = false
    this.bootstrapGeneration = (this.bootstrapGeneration || 0) + 1
    this.bootstrapped = false
    this.bootstrapping = false
    this.device = null
    this.state = null
    this.account = document.querySelector('meta[name="photo-updates-account"]')?.content
    this.onWake = () => {
      if (document.visibilityState === "hidden") {
        this.wasHidden = true
        return
      }
      if (this.wasHidden) {
        this.wasHidden = false
        deferredAccounts.delete(this.account)
        this.maybeIntroduce()
      }
      this.refresh()
    }
    this.onTabVisible = event => { if (event.detail?.visible) this.refresh() }
    document.addEventListener("native:tab-visibility", this.onTabVisible)
    document.addEventListener("visibilitychange", this.onWake)
    window.addEventListener("focus", this.onWake)
    try {
      if (!document.querySelector('meta[name="photo-updates-account"]')) {
        await this.native("unbind")
        return
      }
      if (!document.querySelector('meta[name="photo-updates-rollout"]')) {
        await this.native("unbind")
        return
      }
      this.element.querySelectorAll("[data-photo-updates-controls]").forEach(el => { el.hidden = false })
      this.element.querySelectorAll("[data-photo-updates-toggle]").forEach(el => { el.disabled = true })
      this.element.querySelectorAll("[data-photo-updates-status]").forEach(el => { el.textContent = "" })
      this.timer = setInterval(() => this.refresh(), 3000)
      await this.bootstrap()
    } catch (error) { this.showError(error) }
    finally { this.finishBootstrap?.() }
  }

  async bootstrap() {
    if (this.disconnected || this.bootstrapping || this.bootstrapped) return
    const generation = this.bootstrapGeneration
    const current = () => !this.disconnected && generation === this.bootstrapGeneration
    this.bootstrapping = true
    try {
      const { device_id } = await this.native("device")
      if (!current()) return
      const device = await this.post("device", { device_id })
      if (!current()) return
      const state = await this.native("bind", device)
      if (!current()) return
      this.device = device
      this.state = state
      this.error = null
      this.bootstrapped = true
      this.render()
      this.maybeIntroduce()
    } catch {
      // Keep transient setup recovery silent; polling and foreground events retry.
    } finally {
      if (current()) this.bootstrapping = false
    }
  }

  disconnect() {
    this.disconnected = true
    this.bootstrapGeneration = (this.bootstrapGeneration || 0) + 1
    clearInterval(this.timer)
    document.removeEventListener("native:tab-visibility", this.onTabVisible)
    document.removeEventListener("visibilitychange", this.onWake)
    window.removeEventListener("focus", this.onWake)
    this.dialog?.close()
  }

  native(action, data = {}) {
    return this.handler.postMessage({ ...data, action })
  }

  async post(action, data, canRefreshToken = true) {
    const account = this.account
    const generation = this.bootstrapGeneration
    const response = await fetch(`/photo_updates/${action}`, {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        "X-Proximity-Photo-Updates": "1" },
      body: JSON.stringify(data)
    })
    if (!response.ok) {
      const failure = response.status === 422 ? await response.json().catch(() => ({})) : {}
      if (canRefreshToken && failure.error === "stale_security_token") {
        const tokenResponse = await fetch("/photo_updates/security_token", {
          credentials: "same-origin", cache: "no-store",
          headers: { "Accept": "application/json", "X-Proximity-Photo-Updates": "1" }
        })
        if (tokenResponse.ok) {
          const fresh = await tokenResponse.json()
          if (!this.disconnected && generation === this.bootstrapGeneration && account === this.account && String(fresh.user_id) === account && fresh.token) {
            const meta = document.querySelector('meta[name="csrf-token"]')
            if (meta) {
              meta.content = fresh.token
              return this.post(action, data, false)
            }
          }
        }
      }
      throw new Error("Could not save photo updates. Please try again.")
    }
    return response.status === 204 ? {} : response.json()
  }

  async chooseImport(event) {
    if (!this.handler || !document.querySelector('meta[name="photo-updates-rollout"]')) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const button = event.currentTarget
    await this.ready
    if (this.disconnected) return
    button.dispatchEvent(new CustomEvent("photo-updates:manual", { bubbles: true }))
  }

  async chooseHomeManual(event) {
    if (!this.handler || !document.querySelector('meta[name="photo-updates-rollout"]')) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const button = event.currentTarget
    await this.ready
    if (this.disconnected || this.busy) return
    try {
      await this.post("introduction", { state: "dismissed" })
      this.device.introduction = "dismissed"
    } catch (error) {
      this.showError(error)
      return
    }
    button.dispatchEvent(new CustomEvent("photo-updates:manual", { bubbles: true }))
  }

  async chooseHomeUpload(event) {
    if (!this.handler || !document.querySelector('meta[name="photo-updates-rollout"]')) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const button = event.currentTarget
    await this.ready
    if (this.disconnected) return
    await this.refresh()
    button.dispatchEvent(new CustomEvent("photo-updates:manual", {
      bubbles: true, detail: { choosePhotos: !!this.state?.enabled }
    }))
  }

  openIntroduction() {
    this.dialog = this.element.querySelector("[data-photo-updates-intro]")
    if (!this.dialog || document.querySelector("dialog[open]")) return
    this.setupStarted = this.device?.introduction === "in_progress"
    this.cancelDrag()
    this.dialog.showModal()
    this.render()
  }

  maybeIntroduce() {
    if (!this.device || this.disconnected || deferredAccounts.has(this.account)) return
    if (shouldIntroduce({ path: location.pathname, introduction: this.device.introduction, hasMatches: this.device.has_matches, hasLibraryPhotos: this.device.has_library_photos })) {
      this.openIntroduction()
    }
  }

  defer(event) {
    event?.preventDefault()
    if (this.busy) return
    deferredAccounts.add(this.account)
    this.dialog?.close()
  }

  startDrag(event) {
    if (this.busy || event.pointerType === "touch" || !event.isPrimary || event.button !== 0) return
    this.dragStart = event.clientY
    this.dragDistance = 0
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  startTouch(event) {
    if (this.busy || event.touches.length !== 1) return
    event.preventDefault()
    this.dragStart = event.touches[0].clientY
    this.dragDistance = 0
  }

  moveTouch(event) {
    if (this.dragStart == null) return
    event.preventDefault()
    this.drag(event.touches[0])
  }

  drag(event) {
    if (this.dragStart == null) return
    this.dragDistance = Math.max(0, event.clientY - this.dragStart)
    this.dialog.style.transform = `translateY(${this.dragDistance}px)`
  }

  endDrag() {
    const dismiss = this.dragDistance >= 80
    this.cancelDrag()
    if (dismiss) this.defer()
  }

  cancelDrag() {
    this.dragStart = null
    this.dragDistance = 0
    if (this.dialog) this.dialog.style.transform = ""
  }

  async toggle(event) {
    if (event.target.checked) {
      event.target.checked = false
      await this.enable()
    } else {
      try { this.state = await this.native("pause"); this.render() }
      catch (error) { await this.refresh(); this.showError(error) }
    }
  }

  async enable() {
    if (this.busy) return
    this.error = null
    this.busy = true
    this.render()
    try {
      await this.post("introduction", { state: "in_progress" })
      this.device.introduction = "in_progress"
      this.setupStarted = true
      this.state = await this.native("permission")
      if (this.state.permission !== "full") return
      this.state = await this.native("enable")
      this.device.introduction = "completed"
      this.dialog?.close()
    } catch (error) { this.showError(error) }
    finally { this.busy = false; this.render() }
  }

  async dismiss() {
    if (this.busy) return
    try {
      await this.post("introduction", { state: "dismissed" })
      this.device.introduction = "dismissed"
      this.dialog?.close()
    } catch (error) { this.showError(error) }
  }

  async manual(event) {
    event.preventDefault()
    const href = event.currentTarget.href
    await this.dismiss()
    if (!this.dialog?.open) window.Turbo.visit(href)
  }

  settings() { this.native("settings").catch(error => this.showError(error)) }

  async refresh() {
    if (this.disconnected || !this.handler) return
    if (!document.querySelector('meta[name="photo-updates-account"]') || !document.querySelector('meta[name="photo-updates-rollout"]')) return
    if (!this.bootstrapped) return this.bootstrap()
    try { this.state = await this.native("status"); this.render() }
    catch (error) { this.showError(error) }
  }

  async retryImport() {
    if (this.busy || !this.state?.enabled) return
    this.busy = true
    this.error = null
    this.render()
    try { this.state = await this.native("enable") }
    catch (error) { this.showError(error) }
    finally { this.busy = false; this.render() }
  }

  renderHome() {
    const status = homeImportStatus({ ...this.state, error: this.error || this.state.error }, this.device?.has_library_photos)
    const choice = !this.state.enabled
    this.element.querySelectorAll("[data-home-auto-import-on]").forEach(el => { el.hidden = !this.state.enabled })
    this.element.querySelectorAll("[data-home-upload-label]").forEach(el => { el.textContent = this.state.enabled ? "Choose photos manually" : "Upload photos" })
    this.element.querySelectorAll("[data-home-import-status]").forEach(el => { el.hidden = !status })
    this.element.querySelectorAll("[data-home-import-manual]").forEach(el => { el.hidden = !!status })
    this.element.querySelectorAll("[data-home-import-choice]").forEach(el => { el.hidden = !choice })
    this.element.querySelectorAll("[data-home-import-fallback]").forEach(el => { el.hidden = choice })
    this.element.querySelectorAll("[data-home-import-enable], [data-home-import-choose-manual]").forEach(el => { el.disabled = this.busy })
    this.element.querySelectorAll("[data-home-import-title]").forEach(el => { el.textContent = status?.title || "" })
    this.element.querySelectorAll("[data-home-import-description]").forEach(el => { el.textContent = status?.description || "" })
    this.element.querySelectorAll("[data-home-import-retry]").forEach(el => { el.hidden = !status?.retry; el.disabled = this.busy })
    this.element.querySelectorAll("[data-home-import-settings]").forEach(el => { el.hidden = !status?.settings })
  }

  render() {
    if (!this.state || this.disconnected) return
    const state = this.state
    this.renderHome()
    this.element.querySelectorAll("[data-photo-updates-toggle]").forEach(el => {
      el.checked = state.enabled
      el.disabled = this.busy
    })
    this.element.querySelectorAll("[data-photo-updates-enable]").forEach(el => {
      el.disabled = this.busy
      el.hidden = (this.setupStarted || !this.dialog?.open) && ["limited", "denied", "restricted"].includes(state.permission)
      el.textContent = this.device?.introduction === "in_progress" ? "Continue" : "Turn on automatic import"
    })
    this.element.querySelectorAll("[data-photo-updates-settings]").forEach(el => {
      el.hidden = !["limited", "denied"].includes(state.permission) || (el.closest?.("dialog") && !this.setupStarted)
    })
    this.element.querySelectorAll("[data-photo-updates-permission]").forEach(el => { el.textContent = this.error || ((this.setupStarted || !el.closest?.("dialog")) && state.permission !== "full" ? permissionText(state.permission) : "") })
    const completed = state.last_completed ? new Date(state.last_completed).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "Not yet"
    this.element.querySelectorAll("[data-photo-updates-access]").forEach(el => {
      el.textContent = { full: "Full Access", limited: "Selected Photos", denied: "Off", restricted: "Restricted", not_determined: "Not requested" }[state.permission] || "Unavailable"
    })
    this.element.querySelectorAll("[data-photo-updates-last-update]").forEach(el => { el.textContent = completed })
    const progress = this.busy ? "Updating…" : state.running ? `Importing · ${state.pending} remaining` : state.enabled ? "" : "Paused. Previous imports are kept."
    // Older native builds put this scheduling warning in the import-error field.
    // Keep foreground progress independent without hiding actual import errors.
    const schedulingUnavailable = state.error === "Background scheduling unavailable. Open the app to catch up."
    const importError = schedulingUnavailable ? "" : state.error
    const background = state.enabled && (schedulingUnavailable || !state.background_available) ? "Background updates are unavailable. Photos still update while Proximity is open." : ""
    this.element.querySelectorAll("[data-photo-updates-status]").forEach(el => {
      el.textContent = [this.error, importError, progress, background, state.pause_pending ? "Pause saved on this phone; server confirmation pending." : ""].filter(Boolean).join(" ")
    })
    this.element.querySelectorAll("[data-photo-updates-diagnostics]").forEach(el => {
      const counts = state.last_run_detected == null ? "" : ` Last run counts: detected ${state.last_run_detected}, skipped (unavailable or missing date/location) ${state.last_run_skipped}, already imported ${state.last_run_already_imported}, uploaded and finalized ${state.last_run_uploaded}. Last scan: ${state.last_scan_recovery ? "recovery" : "changes"}. Last recovery: ${state.last_reconciled || "never"}.`
      const scheduling = state.background_request_pending == null ? "" : ` Background request: ${state.background_request_pending ? "pending" : "none recorded"}. Earliest requested wake (not guaranteed): ${state.background_earliest_wake || "no delay recorded"}. Last scheduled: ${state.background_scheduled_at || "never"}. Scheduling error: ${state.background_scheduling_error || "none"}.`
      el.textContent = `Last run: ${state.last_source || "none"}. Started: ${state.last_started || "never"}. Completed: ${state.last_completed || "never"}. Confirmed photos (total): ${state.uploaded}.${counts}${scheduling} iOS background wake: ${state.last_scheduled_wake || "never"}. Background completion: ${state.last_scheduled_completion || "never"}.`
    })
  }

  showError(error) {
    if (this.disconnected) return
    this.error = error.message || String(error)
    if (this.state) this.renderHome()
    this.element.querySelectorAll("[data-photo-updates-permission], [data-photo-updates-status]").forEach(el => { el.textContent = error.message || String(error) })
  }
}
